import { Suspense, lazy, useEffect, useState, useRef } from 'react';
import { Switch, Route, useLocation } from 'wouter';
import { RouteRegistry, RouteConfig } from './RouteRegistry';
import { useAuth } from '@/hooks/use-auth';
import { useProfileCompletion } from '@/hooks/use-profile-completion';
import { useSubdomain } from '@/contexts/SubdomainContext';
import { shouldRedirectRoute, isRouteAllowedOnSubdomain } from '@/lib/subdomain';
import { redirectToAuthPortal } from '@/lib/authRedirect';
import { isAdmin } from '@/lib/permissions';
import Layout from '@/components/layout/Layout';
import AdminLayout from '@/components/admin/AdminLayout';
import PortalLayout from '@/components/portal/PortalLayout';
import MololinkLayout from '@/components/mololink/MololinkLayout';
import { useOnboarding } from '@/components/portal/OnboardingWizard';
import { Loader2 } from 'lucide-react';

const OnboardingWizard = lazy(() => import('@/components/portal/OnboardingWizard'));
const AdminPublicLayout = lazy(() => import('@/components/admin/AdminPublicLayout'));

import './main.routes';
import './auth.routes';
import './portal.routes';
import './admin.routes';
import './services.routes';
import './ecosystem.routes';
import './mololink.routes'; // Re-enabled for Phase N.1 Decision Layer pages
import './brandbook.routes';

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#080d19]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

interface RouteWrapperProps {
  route: RouteConfig;
  children: React.ReactNode;
}

const SSO_RETRY_INTERVAL_MS = 1500;
const SSO_MAX_WAIT_MS = 10000;

const RouteWrapper: React.FC<RouteWrapperProps> = ({ route, children }) => {
  const { user, isLoading, hasFetched, refetch } = useAuth();
  const { needsCompletion, isLoading: profileLoading } = useProfileCompletion();
  const { subdomain } = useSubdomain();
  const [location, setLocation] = useLocation();
  const ssoRetryStartRef = useRef<number | null>(null);
  const [ssoTimedOut, setSsoTimedOut] = useState(false);

  const isSSOCallback = location.includes('/sso-callback') || location.includes('/sso-bootstrap');

  useEffect(() => {
    if (!isSSOCallback) {
      ssoRetryStartRef.current = null;
      setSsoTimedOut(false);
      return;
    }

    if (user) {
      ssoRetryStartRef.current = null;
      setSsoTimedOut(false);
      return;
    }

    if (isLoading || !hasFetched) {
      return;
    }

    if (!ssoRetryStartRef.current) {
      ssoRetryStartRef.current = Date.now();
    }

    const elapsed = Date.now() - ssoRetryStartRef.current;
    if (elapsed >= SSO_MAX_WAIT_MS) {
      setSsoTimedOut(true);
      return;
    }

    const timer = setTimeout(() => {
      refetch();
    }, SSO_RETRY_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [isSSOCallback, user, isLoading, hasFetched, refetch]);

  if (!isRouteAllowedOnSubdomain(route.subdomain, subdomain)) {
    const redirect = shouldRedirectRoute(location, route.subdomain);
    if (redirect.shouldRedirect && redirect.targetUrl) {
      window.location.href = redirect.targetUrl;
      return <LoadingSpinner />;
    }
  }

  if (route.requireAuth) {
    if (isLoading || !hasFetched) {
      return <LoadingSpinner />;
    }

    if (!user) {
      if (isSSOCallback && !ssoTimedOut) {
        return <LoadingSpinner />;
      }
      redirectToAuthPortal('/login');
      return <LoadingSpinner />;
    }
  }

  if (route.requireAdmin && !isLoading && hasFetched && user && !isAdmin(user.role)) {
    window.location.href = '/';
    return null;
  }

  const normalizedPath = location.replace(/\/$/, '') || '/';
  const isCompleteProfilePage = normalizedPath === '/complete-profile';
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(normalizedPath);
  const isPublicPage = ['/', '/about', '/contact', '/services', '/partners', '/terms', '/privacy'].includes(normalizedPath) || normalizedPath.startsWith('/services/');
  
  const isAdminRole = user && isAdmin(user.role);
  if (
    !isLoading &&
    !profileLoading &&
    user &&
    needsCompletion &&
    !isAdminRole &&
    !isCompleteProfilePage &&
    !isAuthPage &&
    !isPublicPage &&
    route.requireAuth
  ) {
    setLocation('/complete-profile');
    return <LoadingSpinner />;
  }

  switch (route.layout) {
    case 'admin':
      return <AdminLayout>{children}</AdminLayout>;
    case 'admin-public':
      return <AdminPublicLayout>{children}</AdminPublicLayout>;
    case 'portal':
      return <PortalLayout>{children}</PortalLayout>;
    case 'mololink':
      return <MololinkLayout>{children}</MololinkLayout>;
    case 'none':
      return <>{children}</>;
    case 'default':
    default:
      return <Layout>{children}</Layout>;
  }
};

const PortalOnboardingWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showOnboarding, completeOnboarding } = useOnboarding();
  const { subdomain } = useSubdomain();
  const { user } = useAuth();
  
  // Only show onboarding on app subdomain for authenticated users
  const shouldShowOnboarding = showOnboarding && subdomain === 'app' && !!user;
  
  return (
    <>
      {children}
      {shouldShowOnboarding && (
        <Suspense fallback={null}>
          <OnboardingWizard onComplete={completeOnboarding} onSkip={completeOnboarding} />
        </Suspense>
      )}
    </>
  );
};

export const AppRouter: React.FC = () => {
  const { subdomain } = useSubdomain();
  const routes = RouteRegistry.getRoutes(subdomain);

  return (
    <PortalOnboardingWrapper>
      <Suspense fallback={<LoadingSpinner />}>
        <Switch>
          {routes.map((route) => {
            const Component = route.component;
            return (
              <Route key={route.path} path={route.path}>
                <RouteWrapper route={route}>
                  <Component />
                </RouteWrapper>
              </Route>
            );
          })}
        </Switch>
      </Suspense>
    </PortalOnboardingWrapper>
  );
};